import { formatMoney, formatQuantity, useLocale, useTranslation } from '@multaqa/i18n'
import { Button } from '@multaqa/ui/components/ui/button'
import { Combobox, type ComboboxOption } from '@multaqa/ui/components/ui/combobox'
import { cn } from '@multaqa/ui/lib/utils'
import { type ComponentProps, useState } from 'react'
import {
  type CatalogProductType,
  type CatalogVariant,
  type CatalogVariantStatus,
  catalogVariantMatches,
  SUPPLIER_LIST_SIZE,
  useCatalogVariantSearch,
  useSelectedCatalogVariant,
  useSettledSearch,
} from '@/components/catalog-item-search'
import {
  type CatalogStockGroup,
  type CatalogStockState,
  groupOptionsByStock,
} from '@/components/catalog-stock-groups'
import { InlineQueryError } from '@/components/inline-query-error'
import { localizedText } from '@/lib/me'

export type { CatalogVariant } from '@/components/catalog-item-search'

export interface CatalogItemPickerProps {
  companyId: string | null
  /** The chosen variant's id, or ''. */
  value: string
  onSelect: (variant: CatalogVariant) => void
  /** Given, the picker offers a clear action that calls it. */
  onClear?: () => void
  /** Shown for a chosen item no loaded page holds, e.g. its SKU. */
  selectedLabel?: string
  /** A code (SKU) the picker searches by to find a chosen item it has not loaded. */
  selectedLookup?: string
  status?: CatalogVariantStatus
  productType?: CatalogProductType
  /** Which price each row shows, if any. */
  price?: 'sales' | 'purchase'
  /** Only this supplier's products (a purchase line); '' lists nothing. */
  supplierId?: string
  /** Shown when the list is empty, in place of the generic "nothing found". */
  emptyLabel?: string
  /**
   * Given, every item is listed under its stock in the line's warehouse and
   * only an item in stock there can be picked (a sales line).
   */
  stockState?: (row: CatalogVariant) => CatalogStockState
  /** The field cannot be opened yet, e.g. until the order has a warehouse. */
  disabled?: boolean
  id?: string
  placeholder?: string
  clearLabel?: string
  loadErrorLabel?: string
  className?: string
  onCloseAutoFocus?: ComponentProps<typeof Combobox>['onCloseAutoFocus']
  'aria-label'?: string
  'aria-invalid'?: boolean
  'data-testid'?: string
}

const STOCK_KEYS: Record<CatalogStockGroup, string> = {
  in_stock: 'inStock',
  out_of_stock: 'outOfStock',
  not_here: 'notHere',
}

export function catalogVariantName(variant: CatalogVariant, locale: string): string {
  return (
    localizedText(variant.name, locale) ||
    localizedText(variant.product.name, locale) ||
    variant.sku
  )
}

/**
 * A document line's description for a picked item: its name, then its part
 * number when that differs from its SKU (the rule the API uses for invoices
 * raised from a sales order).
 */
export function catalogLineDescription(variant: CatalogVariant, locale: string): string {
  const name = catalogVariantName(variant, locale)
  const partNumber = variant.effective_part_number
  return partNumber && partNumber !== variant.sku ? `${name} · ${partNumber}` : name
}

/**
 * The one item picker for every line that names a stock item (ticket 02).
 *
 * Search runs on the server (`GET /directory/variants`), which matches part
 * number, SKU, barcode and name, so the list is shown unfiltered: a part
 * number match would otherwise vanish behind a label that does not show it.
 * "Load more" walks the pages, and the chosen item stays visible after a new
 * search because the picker remembers it, or looks it up by `selectedLookup`.
 */
export function CatalogItemPicker({
  companyId,
  value,
  onSelect,
  onClear,
  selectedLabel,
  selectedLookup,
  status = 'active',
  productType,
  price,
  supplierId,
  emptyLabel,
  stockState,
  disabled,
  id,
  placeholder,
  clearLabel,
  loadErrorLabel,
  className,
  onCloseAutoFocus,
  ...aria
}: CatalogItemPickerProps) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const [text, setText] = useState('')
  const [picked, setPicked] = useState<CatalogVariant | null>(null)
  const search = useSettledSearch(text)
  // A supplier's list is loaded once and searched as it is typed; the server
  // is asked only when the list is longer than one load, or nothing matches.
  const supplierList = useCatalogVariantSearch(
    companyId,
    { status, productType, supplierId, perPage: SUPPLIER_LIST_SIZE },
    supplierId !== undefined,
  )
  const listPage = supplierList.data?.pages[0]
  const wholeList =
    listPage && listPage.meta.current_page >= listPage.meta.last_page ? listPage.data : null
  const listed = wholeList?.filter((row) => catalogVariantMatches(row, text)) ?? null
  const askServer = listed === null || (text.trim() !== '' && listed.length === 0)
  const results = useCatalogVariantSearch(
    companyId,
    { search, status, productType, supplierId },
    askServer &&
      (supplierId === undefined ||
        (supplierList.isSuccess && (wholeList === null || search !== ''))),
  )
  const rows = askServer ? (results.data?.pages.flatMap((page) => page.data) ?? []) : (listed ?? [])
  const shown = askServer ? results : supplierList
  const loaded = askServer ? results.isSuccess : supplierList.isSuccess
  const fetching = supplierList.isFetching || (askServer && results.isFetching)
  const known = [...rows, ...(picked ? [picked] : [])]
  const missing = value !== '' && !known.some((row) => row.id === value)
  const selected = useSelectedCatalogVariant(companyId, value, selectedLookup, missing && loaded)
  if (selected.data) known.push(selected.data)

  const entries: { option: ComboboxOption; state: CatalogStockState }[] = []
  const seen = new Set<string>()
  for (const row of known) {
    if (seen.has(row.id)) continue
    seen.add(row.id)
    const state = stockState?.(row) ?? { group: 'in_stock' }
    entries.push({ option: variantOption(row, state), state })
  }
  const options: ComboboxOption[] = stockState
    ? groupOptionsByStock(entries, value, (group) => t(`catalogPicker.stock.${STOCK_KEYS[group]}`))
    : entries.map((entry) => entry.option)
  if (value !== '' && !seen.has(value) && selectedLabel) {
    options.push({ value, label: selectedLabel })
  }

  function variantOption(row: CatalogVariant, state: CatalogStockState): ComboboxOption {
    const name = catalogVariantName(row, locale)
    const partNumber = row.effective_part_number
    const amount =
      price === 'sales'
        ? formatMoney(row.sales_price, row.sales_price_currency, locale)
        : price === 'purchase'
          ? formatMoney(row.purchase_price, row.purchase_price_currency, locale)
          : null
    const description = [
      row.sku,
      partNumber ? t('catalogPicker.partNumber', { value: partNumber }) : null,
      amount,
      state.available !== undefined
        ? t('catalogPicker.stock.available', { quantity: formatQuantity(state.available, locale) })
        : null,
    ]
      .filter(Boolean)
      .join(' · ')
    return { value: row.id, label: name, displayLabel: name, description }
  }

  const footer =
    askServer && results.hasNextPage ? (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="w-full justify-start"
        disabled={results.isFetchingNextPage}
        onClick={() => void results.fetchNextPage()}
        data-testid="catalog-picker-load-more"
      >
        {results.isFetchingNextPage ? t('catalogPicker.loadingMore') : t('catalogPicker.loadMore')}
      </Button>
    ) : null

  return (
    <div className={cn('grid min-w-0 gap-2', className)}>
      <Combobox
        id={id}
        value={value}
        options={options}
        filter={false}
        footer={footer}
        onCloseAutoFocus={onCloseAutoFocus}
        onSearchChange={setText}
        onValueChange={(next) => {
          if (next === '') {
            onClear?.()
            return
          }
          const row = known.find((candidate) => candidate.id === next)
          if (!row) return
          setPicked(row)
          onSelect(row)
        }}
        placeholder={placeholder ?? t('catalogPicker.placeholder')}
        searchPlaceholder={t('catalogPicker.search')}
        emptyLabel={
          fetching ? t('catalogPicker.searching') : (emptyLabel ?? t('catalogPicker.empty'))
        }
        clearable={Boolean(onClear) && !disabled}
        disabled={disabled}
        clearLabel={clearLabel ?? t('catalogPicker.clear')}
        className="w-full min-w-0"
        {...aria}
      />
      {shown.isError ? (
        <InlineQueryError
          title={t('error.asyncTitle')}
          description={loadErrorLabel ?? t('catalogPicker.loadError')}
          retryLabel={t('action.retry')}
          onRetry={() => void shown.refetch()}
        />
      ) : null}
    </div>
  )
}
